/**
 * System-agnostic engine — meta-model types (v0.1, provisional).
 *
 * These describe the shape of a *content pack*: a game declared as data. The engine
 * (`src/engine/createEngine.ts`) is pure TypeScript and evaluates these against a flat
 * scope, with no knowledge of D&D. See `docs/ENGINE_SPEC.md`.
 */

/** Flat evaluation scope. Only scalars cross the engine boundary (booleans pass as 1 | 0). */
export type Scope = Record<string, number | string>

/** A reusable helper function: an expression over named params (e.g. `mod`, `prof`). */
export interface FunctionRule {
  params: string[]
  expr: string
}

/** A derived stat: a single expression evaluated against a scope. */
export interface DerivedRule {
  expr: string
  /** Human-facing note / manual provenance. */
  note?: string
}

/** Layer A — the vocabulary a system declares. Minimal for the v0.1 slice. */
export interface Vocabulary {
  abilities: readonly string[]
  resources: readonly string[]
  progression: { axis: string; min: number; max: number }
}

/**
 * Layer C — a single declared choice the player makes inside a step.
 *
 * Descriptive metadata: it drives the generic choice picker's rendering. The engine does
 * not read `source`/`count` to *validate* a step — validity is the step's `valid` predicate
 * (host reduces selection state to scalars, the engine evaluates the predicate). It *does*
 * read `source`/`eligible` to *resolve options* (see `Engine.resolveOptions`).
 * See `docs/ENGINE_SPEC.md` §6.
 */
export interface ChoicePoint {
  /** Stable id (also the key under which the host stores the selection). */
  id: string
  /** `single` = pick exactly one option; `multi` = pick `count` distinct options. */
  kind: 'single' | 'multi'
  /**
   * The option-set this choice draws from, in the closed `static:`/`ref:` grammar:
   * - `static:<setName>` — a system-declared set independent of prior choices (e.g. `static:origins`).
   * - `ref:<selectionId>.<field>` — a set derived from an earlier selection (e.g. `ref:class.skillOptions`).
   * Parsed at pack-load into `ParsedSource`; an unrecognised kind throws (loud-fail). See `parseSource`.
   */
  source: string
  /** Required selections: a fixed number, or a scope variable name resolved at runtime. */
  count?: number | string
  /**
   * Optional `expr-eval` boolean predicate evaluated **per candidate option** against the
   * scalar scope merged with the candidate's own scalar fields (and `candidateId`). A truthy
   * result ⇒ the option is selectable. Absent ⇒ every option is selectable. Reuses the same
   * evaluator as `valid` predicates — no new language features. See `Engine.resolveOptions`.
   */
  eligible?: string
  /** Human-facing label / manual provenance. */
  label?: string
}

/** Layer C — one declared step of the character-creation flow. */
export interface FlowStep {
  /** Stable id used by `Engine.validateStep`. */
  id: string
  /** 1-based position in the wizard. */
  order: number
  title: string
  /** What the player chooses here (drives the generic choice picker). */
  choices: ChoicePoint[]
  /** Validity predicate: a boolean expression over the flat scalar scope the host supplies. */
  valid: string
}

/** Layer C — the declared character-creation flow (the 7-step wizard as data). */
export interface CreationFlow {
  steps: FlowStep[]
}

/**
 * Optional declaration of the host↔pack scope contract (G9, ADR-026 Option B, schema 1.1.0).
 *
 * Every flow predicate reads scalars the *host* must put in scope; without a declaration a
 * missing key surfaces only as expr-eval behaviour at evaluate-time. When `scopeContract` is
 * present, `validatePack` cross-checks every flow predicate's free variables against it —
 * loud, path-precise failure at engine creation instead of silent misbehaviour.
 */
export interface ScopeContract {
  /**
   * Host-supplied scalar scope keys the flow predicates (`valid`, string `count`, `eligible`)
   * read, mapped to a human-facing type descriptor (e.g. `'number'`, `'boolean-as-1|0'`).
   * Descriptors are documentation for the host author; the cross-check uses only the keys.
   */
  scope: Record<string, string>
  /**
   * Per choice id: the candidate scalar fields that choice's `eligible` predicate may read
   * (the per-candidate scope the engine builds in `resolveOptions`; `candidateId` is always
   * injected and need not be declared). Keys must be declared choice ids.
   */
  candidateFields?: Record<string, readonly string[]>
}

// ── Layer D — the "play" meta-model (game/turn model as data) ─────────────────────────────

/**
 * Layer D §2.3 — the closed set of turn-ordering axes the engine honours. As with the Layer C
 * `SOURCE_KINDS`, **the closed set equals the implemented set**: an unrecognised mode throws
 * (loud-fail) rather than being silently mis-sorted. `sides` (side-based alternating activation,
 * DESIGN §2.3) is a *planned future family* — it needs per-actor side-membership state Layer D
 * does not yet model, so it is added under a later versioned bump, not stubbed here (INC-045).
 */
export const INITIATIVE_MODES = ['rolled', 'fixed'] as const
export type InitiativeMode = (typeof INITIATIVE_MODES)[number]

/**
 * Layer D §2.3 — the declared turn-ordering axis. `sortTurnOrder` (pure) dispatches on `mode`:
 * `rolled` and `fixed` share the numeric-desc / nulls-last / id-tiebreak comparator (they differ
 * only *upstream* in how each actor's ordering number is produced, which the sort never does).
 * `expr` computes that per-actor key (`rolled` = a die roll + mods; `fixed` = a static stat);
 * it is validated to parse under expr-eval but consumed by the host's roll flow, not by the sort.
 */
export type Initiative = { mode: InitiativeMode; expr: string }

/**
 * Layer D §2.4 — the closed set of turn-lifecycle events the engine's condition clock understands.
 * As with `INITIATIVE_MODES`, **the closed set equals the implemented set**: these are exactly the
 * events `useCombatTracker` emits (an unrecognised `on` throws at `validatePack`, never silently
 * misbehaves). A declared {@link Expiry} keys off one of these. `turn-start` is included because a
 * `sustain` fires there; no expiry need declare it (an event may be in the set yet used by nothing).
 */
export const LIFECYCLE_EVENTS = [
  'turn-start',
  'turn-end',
  'round-advance',
  'encounter-end',
  'save',
] as const
export type LifecycleEvent = (typeof LIFECYCLE_EVENTS)[number]

/**
 * Layer D §2.4 — one declared expiry kind: its `id` (referenced by an effect's persisted expiry) and
 * the {@link LifecycleEvent} on which it expires. The four historical D&D expiries
 * (`end-of-next-turn | end-of-encounter | save-ends | rounds`) become four declared entries; the
 * pack-free dispatcher (`src/lib/conditionLifecycle.ts`) removes an effect when its expiry's `on`
 * fires. Closes ENGINE_SPEC §6 open item (c) (ADR-032).
 */
export interface Expiry {
  id: string
  on: LifecycleEvent
  /**
   * `'rounds'` = a counted duration checked against the round clock (drops once `count` rounds have
   * passed since it was applied); absent / `'none'` = fires unconditionally the first time `on` occurs.
   */
  count?: 'rounds' | 'none'
}

/** Layer D §2.4 — the declared condition/effect-duration model (the expiry vocabulary as data). */
export interface ConditionModel {
  expiries: Expiry[]
}

/**
 * Layer D §2.1 — the closed set of dice-resolution FAMILIES the engine honours (ADR-031, schema 1.4.0).
 * As with `INITIATIVE_MODES`/`LIFECYCLE_EVENTS`, **the closed set equals the implemented set**: an
 * unrecognised family throws at `validatePack` (never silently mis-rolled). All three are adopted now
 * (maintainer-confirmed, ADR-031 Decision) — `d20` (dnd-4.5) and `sum` (synth-2d6) are exercised today;
 * `pool` is expressible/validated and awaits a pack that needs it. Adding a *fourth* family is a
 * deliberate, versioned engine change (a `SUPPORTED_SCHEMA_VERSION` major bump), same rule as a `source` kind.
 */
export const DICE_FAMILIES = ['d20', 'sum', 'pool'] as const
export type DiceFamily = (typeof DICE_FAMILIES)[number]

/**
 * Layer D §2.1 — a declared dice model, one of the closed {@link DICE_FAMILIES}. This is *metadata*
 * describing how the host rolls (the engine never rolls dice); the host reduces a roll to the scalars
 * a {@link ResolutionRule} reads. `d20` = roll+mod vs a target number; `sum` = add N dice groups (2d6…);
 * `pool` = count dice ≥ `success` (FitD/PbtA-ish).
 */
export type DiceModel =
  | { family: 'd20'; sides: 20 }
  | { family: 'sum'; dice: { count: number; sides: number }[] }
  | { family: 'pool'; die: number; success: number }

/**
 * Layer D §2.1 — how a rolled result becomes an outcome. `dice` refs a declared {@link DiceModel} in
 * `play.dice`; `scope` names the scalars the host injects (e.g. `['total','target']` for a d20 attack,
 * `['total']` for a 2d6 move); `outcomes` is an ordered ladder whose first `Boolean(when)`-truthy entry
 * wins. `when` is an `expr-eval` predicate over `scope` — reuses the Layer B evaluator, NO new language.
 * `validatePack` checks the `dice` ref resolves and every `when` reads only declared `scope` scalars.
 */
export interface ResolutionRule {
  /** Ref into `play.dice` (the {@link DiceModel} this rule resolves against). */
  dice: string
  /** Scalars the host injects into the `when` scope (e.g. `total`, `target`, `successes`). */
  scope: string[]
  /** Ordered outcome ladder; the first entry whose `when` predicate is truthy wins. */
  outcomes: { id: string; when: string }[]
}

/**
 * Layer D §2.2 (ADR-030, schema 1.5.0) — one declared action kind and its per-turn budget. `id` is an
 * OPEN value (pack-authored, e.g. `standard`/`move`/`minor`/`reaction` in 4e), NOT a closed engine enum —
 * mirrors dice-model names, unlike the closed `INITIATIVE_MODES`/`LIFECYCLE_EVENTS`/`DICE_FAMILIES` sets.
 * `perTurn` is the count a future runtime would track as a scalar budget (B5 is **declare-only**: no
 * runtime consumer exists yet — see `docs/archive/DESIGN_layer_d_b5.md`).
 */
export interface ActionKind {
  id: string
  perTurn: number
  label: string
}

/**
 * Layer D §2.2 — the declared action economy: the counted kinds plus optional `downgrades`, the declared
 * convertibility edges (4e: a standard may be spent as a move). Each edge's `from`/`to` must reference a
 * declared {@link ActionKind.id} — cross-checked in `crossCheckPlay` (an undeclared ref is a loud-fail at
 * engine creation, same discipline as a `resolution.*.dice` ref).
 */
export interface ActionEconomy {
  kinds: ActionKind[]
  downgrades?: { from: string; to: string }[]
}

/**
 * Layer D §2.5 (ADR-030, schema 1.5.0) — a minimal progress clock: a segmented counter that advances on a
 * declared lifecycle event or by hand (`'manual'`). Kept deliberately minimal this pass (declare-only; no
 * runtime consumer — persisted like the turn pointer once one lands, §2.6/B6). `advancesOn` reuses the
 * closed {@link LIFECYCLE_EVENTS} set ∪ the literal `'manual'` (closed == implemented); richer clock
 * semantics (linked/countdown clocks) are deferred until a pack needs them (GAPS D7, open Q3 resolution).
 */
export interface Clock {
  id: string
  segments: number
  advancesOn?: LifecycleEvent | 'manual'
}

/**
 * Layer D §2.7 (ADR-086, schema 1.6.0) — the closed set of recovery-effect kinds the host implements.
 * As with `INITIATIVE_MODES`/`LIFECYCLE_EVENTS`, **the closed set equals the implemented set**: an
 * unrecognised `kind` throws at `validatePack` rather than being silently ignored. The keys, ids and
 * status ids an effect names are OPEN pack values the host checks (`src/lib/recovery.ts`).
 */
export const RECOVERY_EFFECTS = [
  'end-encounter', // clear initiative keys, round-scoped effects, turn pointer, manual order
  'end-combat', // combat phase → idle
  'reset-usage', // drop the named host usage keys (open values, host-checked)
  'restore-hp', // drop `currentHp` (absent = full)
  'restore-abilities', // drop `abilityDamage` (absent = none)
  'clear-effects', // drop active effects: every one ('all') or those with the named status ids
] as const

/**
 * Layer D §2.7 (ADR-086) — the closed set of scopes a recovery's actor restores apply to. Closed
 * like the effect kinds above; absent on a {@link Recovery} ⇒ `'all'`.
 */
export const RECOVERY_ACTORS = ['pcs', 'all'] as const

/** Layer D §2.7 — one recovery effect: a single thing a recovery action does. */
export type RecoveryEffect =
  | { kind: 'end-encounter' }
  | { kind: 'end-combat' }
  | { kind: 'reset-usage'; keys: string[] }
  | { kind: 'restore-hp' }
  | { kind: 'restore-abilities' }
  | { kind: 'clear-effects'; statusIds: 'all' | string[] }

/**
 * Layer D §2.7 (ADR-086, schema 1.6.0) — one pack-declared recovery action (a rest, a night's
 * sleep, a week with a healer). `id`, `name`, `hint`, the usage keys and the status ids are OPEN
 * values; the effect kinds and the object's own keys are closed. The host turns it into a plan and
 * runs the sequence (`src/lib/recovery.ts`); the engine only validates it.
 */
export interface Recovery {
  id: string
  name: string
  hint?: string
  effects: RecoveryEffect[]
  /** Which actors get the actor restores (reset-usage, restore-hp, restore-abilities, clear-effects). Absent ⇒ 'all'. The host maps PC / NPC; end-encounter and end-combat always apply to every row (Q68). */
  actors?: (typeof RECOVERY_ACTORS)[number]
  /** Actors carrying an active effect with one of these status ids get none of the actor restores; end-encounter and end-combat still apply. Absent ⇒ nobody is skipped. */
  skipIfStatus?: string[]
}

/**
 * Layer D — the optional "play" meta-model key on `ContentPack` (sibling of `flow`). A pack with
 * no `play` key is build-only (the engine tolerates its absence exactly as a missing `flow`).
 * Grows over the M4 Phase B increments (B2 `initiative`; B3 `conditions`; B4 `dice`/`resolution`;
 * B5 `actions`/`clocks`).
 */
export interface PlayModel {
  /** The declared turn-ordering axis (§2.3). Absent ⇒ the host defaults to `rolled`. */
  initiative?: Initiative
  /** The declared condition/effect-duration model (§2.4). Absent ⇒ no declared expiry vocabulary. */
  conditions?: ConditionModel
  /** Named dice models (§2.1). Referenced by {@link ResolutionRule.dice}. Absent ⇒ pack declares no dice. */
  dice?: Record<string, DiceModel>
  /** Named resolution rules (§2.1). The host looks one up by id (`engine.resolveOutcome`). Absent ⇒ none. */
  resolution?: Record<string, ResolutionRule>
  /** The declared action economy (§2.2). Absent ⇒ pack declares no per-turn action budget. Declare-only (B5). */
  actions?: ActionEconomy
  /** Declared progress clocks (§2.5). Absent ⇒ pack declares no clocks. Declare-only (B5). */
  clocks?: Clock[]
  /** Pack-declared recovery actions (§2.7). Absent ⇒ none (the GM's ⋯ lists none). */
  recovery?: Recovery[]
}

/** A game expressed as data: instantiates the meta-model for one system. */
export interface ContentPack {
  manifest: { id: string; version: string; schemaVersion: string; license: string }
  vocabulary: Vocabulary
  /** Reusable expression-functions callable from any rule (e.g. `mod(score)`, `prof(tier, level)`). */
  functions: Record<string, FunctionRule>
  /** Named derived stats (Layer B). Reference each other and `functions` by name. */
  derived: Record<string, DerivedRule>
  /** Parametric formulas the host evaluates per-item (host owns iteration, engine owns arithmetic). */
  formulas: Record<string, string>
  /** Layer C — the character-creation flow declared as data. Optional until a system declares it. */
  flow?: CreationFlow
  /** Optional G9 scope-key declaration; when present, `validatePack` cross-checks it. */
  scopeContract?: ScopeContract
  /** Layer D — the "play" meta-model (turn/combat rules as data). Optional until declared. */
  play?: PlayModel
}

// ── Layer C — closed `source` grammar (the engine's known vocabulary) ─────────────────────

/**
 * The closed, versioned set of option-source kinds the engine understands. This is the line
 * between *config* (which sets/fields exist — open) and *code* (which kinds of source the engine
 * can resolve — closed). Adding a kind is a deliberate, versioned engine change, never silent.
 */
export const SOURCE_KINDS = ['static', 'ref'] as const
export type SourceKind = (typeof SOURCE_KINDS)[number]

/** A `ChoicePoint.source` parsed into the closed grammar the engine knows how to resolve. */
export type ParsedSource =
  | { kind: 'static'; set: string }
  | { kind: 'ref'; selection: string; field: string }

/**
 * Parse a `ChoicePoint.source` string into the closed grammar.
 *
 * Loud-fail by design: a missing prefix, an empty set/selection/field, or — critically — an
 * unrecognised kind throws. Silently ignoring a declared-but-unknown source is the worst failure
 * for a product whose promise is "the homebrew rule survives verbatim". See `docs/ENGINE_SPEC.md`.
 */
export function parseSource(source: string): ParsedSource {
  const sep = source.indexOf(':')
  if (sep === -1) throw new Error(`[engine] choice source missing "<kind>:" prefix: "${source}"`)
  const kind = source.slice(0, sep)
  const rest = source.slice(sep + 1)

  if (kind === 'static') {
    if (!rest) throw new Error(`[engine] "static:" source needs a set name: "${source}"`)
    return { kind: 'static', set: rest }
  }
  if (kind === 'ref') {
    const dot = rest.indexOf('.')
    const selection = dot === -1 ? '' : rest.slice(0, dot)
    const field = dot === -1 ? '' : rest.slice(dot + 1)
    if (!selection || !field)
      throw new Error(`[engine] "ref:" source must be "ref:<selection>.<field>": "${source}"`)
    return { kind: 'ref', selection, field }
  }
  throw new Error(
    `[engine] unknown choice source kind "${kind}" in "${source}" (known: ${SOURCE_KINDS.join(', ')})`,
  )
}

// ── Layer C — option resolution (engine composes, host provides raw data) ─────────────────

/**
 * A raw candidate option as the **host** reads it from content (`src/data/*` / the store).
 * Beyond `id`/`label`, descriptive scalar fields (e.g. `meetsPrereqs`, `alreadyOwned`) become
 * variables in the `eligible` scope so prerequisites/graying are declared data, not component code.
 */
export interface RawOption {
  id: string
  label: string
  [key: string]: number | string | boolean | undefined
}

/** A candidate option after the engine has applied the `eligible` predicate. */
export interface ResolvedOption extends RawOption {
  /** True when the `eligible` predicate passed (or there is none). */
  selectable: boolean
  /** Convenience inverse of `selectable` for the renderer. */
  disabled: boolean
}

/**
 * Host-side registry: given a parsed source and the current scope, return the raw candidate list
 * by reading content. This is the **only** seam through which `src/data/*` enters option resolution;
 * the engine itself never imports content. `static:*` resolves a named set; `ref:<sel>.<field>`
 * resolves a field off the selected entity. `choiceId` names the declared choice being resolved, so a
 * provider can compute per-choice candidate fields (`alreadyChosen`) when several choices share one
 * source (ADR-107); providers that do not need it ignore it.
 */
export type OptionProvider = (source: ParsedSource, scope: Scope, choiceId?: string) => RawOption[]
